import React, { useEffect, useMemo } from 'react';

import useSittingStore from '../../../stores/sittingStore';
import useVisitStore from '../../../stores/visitStore';
import useOrgSettingsStore from '../../../stores/orgSettingsStore';
import ProcedureChip from './ProcedureChip';

/**
 * ProcedureChipList — vertical list of ProcedureChip cards inside the
 * Phase 3.0 half-disc widget.
 *
 * Picks the same primary-action heuristic SittingCard uses (in a
 * simplified form): collect_payment for awaiting_payment, execute for
 * payment_cleared/scheduled-today, complete for in_progress, approve for
 * pending_approval. Anything else → no primary action; user opens the
 * full canvas (⤢) for richer flows.
 *
 * Props:
 *   sittings   — Sitting[]
 *   viewer     — { memberId, role }
 *   onAction   — async ({ action, sitting }) => void  (from useSittingActions)
 *   onDelete   — optional (sitting) => void  (renders ✕ on chips)
 *   compact    — bool, switches to icon-only chips for 80px shrunk mode
 *   emptyHint  — string shown when no sittings
 */
// Backend stores all dates in IST (ist_today_str() → 'YYYY-MM-DD').
// `new Date().toISOString()` would compare against UTC, which is wrong
// between 00:00–05:30 IST (UTC date is yesterday). `'en-CA'` formats
// `Intl.DateTimeFormat` output as 'YYYY-MM-DD', matching backend exactly.
function todayInIST() {
  try {
    return new Intl.DateTimeFormat('en-CA', {
      timeZone: 'Asia/Kolkata',
      year: 'numeric', month: '2-digit', day: '2-digit',
    }).format(new Date());
  } catch {
    // Fallback: manual IST offset (UTC+5:30). Same shape, same answer.
    const nowMs = Date.now() + (5.5 * 60 * 60 * 1000);
    return new Date(nowMs).toISOString().slice(0, 10);
  }
}

function isToday(scheduledFor) {
  if (!scheduledFor) return true;
  try {
    const datePart = String(scheduledFor).split('T')[0];
    return datePart <= todayInIST();
  } catch {
    return true;
  }
}

// Stricter sibling for the Resume Visit gate — the server's same-day
// guard rejects cross-day resumes with 409, so we must NOT show the CTA
// on yesterday's completed visits.
function isSameDayAsToday(dateStr) {
  if (!dateStr) return false;
  try {
    const datePart = String(dateStr).split('T')[0];
    return datePart === todayInIST();
  } catch {
    return false;
  }
}

const TERMINAL_STATUSES = new Set(['completed', 'paid', 'cancelled']);

/**
 * Phase 3 — strict-ordering blockers. The backend now owns the rule:
 * any sitting at status === 'held' is, by construction, waiting on a
 * lower-step strict sibling. We just read the row's status and compute
 * which step it's waiting for (the lowest non-terminal sibling) for the
 * 🔒 hint copy.
 *
 * Map shape: { [sitting_id]: { blocked: bool, blockedByStep?: number } }
 */
function computeStrictLocks(sittings) {
  const groups = new Map();
  for (const s of sittings || []) {
    if ((s.plan_ordering_rule || 'flexible') !== 'strict') continue;
    if (s.step_number == null) continue;
    const key = s.source_plan_id || s.plan_id;
    if (!key) continue;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(s);
  }
  const out = {};
  for (const list of groups.values()) {
    list.sort((a, b) => (a.step_number ?? 0) - (b.step_number ?? 0));
    // Find the lowest non-terminal step in the plan — that's the step
    // the held siblings are waiting for.
    let firstOpenStep = null;
    for (const s of list) {
      if (!TERMINAL_STATUSES.has(s.status)) {
        firstOpenStep = s.step_number;
        break;
      }
    }
    for (const s of list) {
      if (s.status !== 'held') continue;
      out[s.sitting_id] = { blocked: true, blockedByStep: firstOpenStep };
    }
  }
  return out;
}

function rolesFor(sitting, viewer, currentVisitDoctorMemberId) {
  const role = (viewer?.role || '').toUpperCase();
  const isAdmin = role === 'ADMIN';
  const isDoctor = role === 'DOCTOR';
  const isReceptionist = role === 'RECEPTIONIST';
  const assignedTo =
    sitting?.assigned_to?.member_id
    || sitting?.assigned_to_member_id
    || null;
  const assignedBy =
    sitting?.assigned_by?.member_id
    || sitting?.assigned_by_member_id
    || null;
  // Phase 1d: sitting.visit_doctor_member_id is the immutable
  // sitting-time visit doctor (audit-stable). Phase 3.4: when the
  // visit's doctor flips on referral, the *current* doctor is also
  // an involved party — passed in via `currentVisitDoctorMemberId`.
  const sittingTimeVisitDoctor = sitting?.visit_doctor_member_id || null;
  const isAssignee = viewer?.memberId && assignedTo === viewer.memberId;
  const isDelegator = viewer?.memberId && assignedBy === viewer.memberId;
  const isApprover = viewer?.memberId && sitting?.approver_member_id === viewer.memberId;
  const isVisitDoctor = (
    viewer?.memberId
    && (
      sittingTimeVisitDoctor === viewer.memberId
      || (currentVisitDoctorMemberId
          && currentVisitDoctorMemberId === viewer.memberId)
    )
  );
  return { isAdmin, isDoctor, isReceptionist, isAssignee, isDelegator, isApprover, isVisitDoctor };
}

function pickPrimary(sitting, viewer, currentVisitDoctorMemberId, visitsById, doctorCanCollect) {
  const status = sitting?.status;
  const r = rolesFor(sitting, viewer, currentVisitDoctorMemberId);

  if (status === 'awaiting_payment' && (r.isReceptionist || r.isAdmin)) {
    return { key: 'collect_payment', label: 'Collect' };
  }
  if (status === 'awaiting_payment' && r.isDoctor) {
    if (doctorCanCollect) return { key: 'collect_payment', label: 'Collect' };
    // Doctor-collect disabled clinic-wide: show a disabled Collect + note.
    return {
      key: 'collect_payment',
      label: 'Collect',
      disabled: true,
      note: 'To start procedure, patient needs to clear payment on Reception.',
    };
  }
  // Phase 3 — `ready` is the post-promotion executable state for
  // non-first strict steps; treated identically to payment_cleared
  // here since the assignee's action (Start) is the same.
  if ((status === 'payment_cleared' || status === 'scheduled' || status === 'ready')
      && isToday(sitting?.scheduled_for)
      && (r.isAssignee || r.isAdmin)) {
    // Resume Visit — when the parent visit is COMPLETED (doctor moved
    // on before payment cleared) and it's the same-day, the assignee
    // needs to re-open the visit before they can execute. Surface the
    // resume CTA in place of Start; useSittingActions routes it
    // through useResumeVisit (conflict-check + optional park). Once
    // resumed, the chip flips back to Start naturally on the next
    // render (visit becomes IN_PROGRESS).
    //
    // Bug A fix: `sitting.visit_status` / `sitting.visit_date` are
    // denormed at fetch time on /sittings/assigned and go stale once
    // the parent visit transitions. Prefer the LIVE row from
    // useVisitStore (kept fresh by VISIT_STATUS_CHANGED) when the
    // parent visit is in the store; fall back to the denorm so chips
    // for visits outside the current store snapshot still work.
    const liveVisit = visitsById?.get?.(sitting.visit_id);
    const effectiveVisitStatus =
      (liveVisit?.status ?? sitting.visit_status ?? '').toUpperCase();
    const effectiveVisitDate = liveVisit?.visit_date ?? sitting.visit_date;
    if (effectiveVisitStatus === 'COMPLETED'
        && isSameDayAsToday(effectiveVisitDate)) {
      return { key: 'resume_visit', label: 'Resume Visit' };
    }
    return { key: 'execute', label: 'Start' };
  }
  if (status === 'in_progress' && (r.isAssignee || r.isAdmin)) {
    return { key: 'complete', label: 'Complete' };
  }
  if (status === 'pending_approval' && (r.isApprover || r.isAdmin)) {
    return { key: 'approve', label: 'Approve' };
  }
  return null;
}

function pickSecondary(sitting, viewer, currentVisitDoctorMemberId) {
  // Reject lives next to Approve so the approver doesn't have to leave
  // the disc for a rejection. All other secondary flows (reassign, plan
  // cancel, prescription edit) live behind the full editor.
  const status = sitting?.status;
  const r = rolesFor(sitting, viewer, currentVisitDoctorMemberId);
  if (status === 'pending_approval' && (r.isApprover || r.isAdmin)) {
    return { key: 'reject', label: 'Reject' };
  }
  return null;
}

function pickNoteAction(sitting, viewer, noteCount, currentVisitDoctorMemberId) {
  // Phase 4.0i — Notes button surfaces for any "involved party" on any
  // sitting that has a bound visit. Mirrors the backend's permission
  // rule (assignee | delegator | approver | visit-doctor | admin) so
  // what the chip offers and what the server accepts stay in lockstep.
  // Routes through useSittingActions' 'write_note' handler which opens
  // ProcedureNoteDialog.
  //
  // Phase 4.0j — terminal status no longer hides the button. Doctors
  // need to read post-procedure documentation and reply with follow-up
  // instructions; the backend (procedure_note_service.add_note) has
  // never gated on status, only on the involved-party rule.
  //
  // Phase 3.4 — `isVisitDoctor` now also matches the *current* visit
  // doctor (not just the immutable sitting-time one) so a referred
  // visit's new doctor sees this chip.
  //
  // `noteCount` is the live thread length from `notesBySitting`; when
  // > 0 we render `📝 Notes (N)` so unread / waiting threads stand out
  // without expanding the chip.
  if (!sitting?.visit_id) return null;
  const r = rolesFor(sitting, viewer, currentVisitDoctorMemberId);
  const isInvolved =
    r.isAssignee
    || r.isDelegator
    || r.isApprover
    || r.isVisitDoctor
    || r.isAdmin;
  if (!isInvolved) return null;
  const label = noteCount > 0 ? `📝 Notes (${noteCount})` : '📝 Notes';
  return { key: 'write_note', label };
}

// Sitting statuses that are terminal — Reassign + Cancel hide once
// the sitting has settled into one of these. Mirrors the backend's
// rejection of cancel/reassign on terminal rows.
const TERMINAL_SITTING_STATUSES = new Set([
  'completed', 'paid', 'cancelled',
]);


export default function ProcedureChipList({
  sittings,
  viewer,
  onAction,
  onDelete,
  compact = false,
  emptyHint = null,
  showPatient = false,
  currentVisitDoctorMemberId = null,
}) {
  // Live note-count projection from the store; chip badges re-render
  // whenever a SITTING_NOTE_ADDED WS event lands. Subscribed before any
  // early return so the Hook order stays stable across renders.
  const notesBySitting = useSittingStore((s) => s.notesBySitting);

  // Doctor-collect policy — gates the Collect button for doctors.
  const doctorCanCollect = useOrgSettingsStore((s) => s.doctorCanCollectPayment);
  const fetchOrgSettings = useOrgSettingsStore((s) => s.fetchProcedurePaymentSettings);
  useEffect(() => { fetchOrgSettings(); }, [fetchOrgSettings]);

  // Bug A fix: subscribe to the live visit list so the chip's Resume
  // Visit decision uses fresh visit_status (kept up-to-date by the
  // realtime middleware on VISIT_STATUS_CHANGED) instead of the
  // denormed value on the sitting row, which goes stale once the
  // parent visit transitions. Keyed map for O(1) lookup per chip.
  const visits = useVisitStore((s) => s.visits);
  const dailyVisits = useVisitStore((s) => s.dailyVisits);
  const visitsById = useMemo(() => {
    const m = new Map();
    const push = (v) => {
      const id = v?.visit_id || v?.id;
      if (id && !m.has(id)) m.set(id, v);
    };
    (visits || []).forEach(push);
    (dailyVisits || []).forEach(push);
    return m;
  }, [visits, dailyVisits]);

  if (!sittings || sittings.length === 0) {
    return emptyHint
      ? <p style={{ margin: 0, fontSize: '0.75rem', opacity: 0.7 }}>{emptyHint}</p>
      : null;
  }

  // Compute strict-ordering locks once for the whole list. Sittings
  // belonging to a strict plan whose lower-numbered sibling is still
  // open render with a lock + "Waiting for step N" hint and no Start.
  const strictLocks = computeStrictLocks(sittings);

  // Presentation order: Running (in progress, ANY assignee) on top →
  // Queue (newest-added first, strict-locked last) → Done (terminal) at
  // the bottom. Purely a render sort; no data/action changes.
  const sectionOf = (s) => {
    if (s?.status === 'in_progress') return 'running';
    if (TERMINAL_STATUSES.has(s?.status)) return 'done';
    return 'queue';
  };
  const rankOf = (s) => {
    const sec = sectionOf(s);
    if (sec === 'running') return 0;
    if (sec === 'done') return 3;
    return strictLocks[s.sitting_id]?.blocked ? 2 : 1; // startable before locked
  };
  const createdMs = (s) => {
    const t = Date.parse(s?.created_at || '');
    return Number.isNaN(t) ? 0 : t;
  };
  // When a terminal item reached its end-state — keeps Done ordered
  // most-recently-finished first instead of following raw array order.
  const doneMs = (s) => {
    const t = Date.parse(s?.completed_at || s?.updated_at || s?.created_at || '');
    return Number.isNaN(t) ? 0 : t;
  };
  // Deterministic order in EVERY section (not just the queue), with a stable
  // id tiebreaker — so a status change moves only the card that changed and its
  // neighbours don't reshuffle on each refresh (kills the "jumping").
  const sorted = [...sittings].sort((a, b) => {
    const ra = rankOf(a);
    const rb = rankOf(b);
    if (ra !== rb) return ra - rb;
    const sec = sectionOf(a);
    let cmp = 0;
    if (sec === 'queue') cmp = createdMs(b) - createdMs(a);        // newest-added on top
    else if (sec === 'done') cmp = doneMs(b) - doneMs(a);          // most-recently-done on top
    else cmp = createdMs(a) - createdMs(b);                        // running: stable by start order
    if (cmp !== 0) return cmp;
    return String(a?.sitting_id || '').localeCompare(String(b?.sitting_id || ''));
  });

  const SECTION_META = {
    running: { label: 'Running', accent: '#2563eb' },
    queue:   { label: 'Queue',   accent: '#0d9488' },
    done:    { label: 'Done',    accent: '#059669' },
  };
  const sectionCounts = sorted.reduce((acc, s) => {
    const k = sectionOf(s);
    acc[k] = (acc[k] || 0) + 1;
    return acc;
  }, {});
  let lastSection = null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: compact ? '0.25rem' : '0.5rem' }}>
      {sorted.map((sitting) => {
        const lockInfo = strictLocks[sitting.sitting_id];
        const isLocked = !!lockInfo?.blocked;
        // Locked sittings hide their primary action — Start only appears
        // once the prior step terminates. Cancel still allowed (server
        // permits it from any non-terminal status). Notes stay visible
        // even when locked — assignee can prep notes ahead of unlock.
        const primary = compact || isLocked ? null : pickPrimary(sitting, viewer, currentVisitDoctorMemberId, visitsById, doctorCanCollect);
        const secondary = compact || isLocked ? null : pickSecondary(sitting, viewer, currentVisitDoctorMemberId);
        const noteCount = notesBySitting?.[sitting.sitting_id]?.length || 0;
        const note = compact ? null : pickNoteAction(sitting, viewer, noteCount, currentVisitDoctorMemberId);
        const handleDelete = onDelete ? () => onDelete(sitting) : null;

        // Overflow menu eligibility — Reassign + Cancel, mirroring
        // the SittingCard rules so the doctor doesn't have to leave
        // the widget for them. Hidden once the sitting is terminal
        // or in compact mode (eg. the half-disc widget where space is
        // tight and the full editor is one click away).
        const r = rolesFor(sitting, viewer, currentVisitDoctorMemberId);
        const isTerminal = TERMINAL_SITTING_STATUSES.has(sitting?.status);
        const canReassign = !compact && !isTerminal && !!onAction
          && (r.isDoctor || r.isAdmin);
        const canCancel = !compact && !isTerminal && !!onAction
          && (r.isDoctor || r.isAdmin || r.isReceptionist);

        // Section header — only in the roomy (non-compact) list. Shows
        // Running / Queue / Done once at each group boundary.
        const section = sectionOf(sitting);
        const showHeader = !compact && section !== lastSection;
        if (showHeader) lastSection = section;
        const headerMeta = SECTION_META[section];

        return (
          <React.Fragment key={sitting.sitting_id}>
            {showHeader && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '0.35rem 0.15rem 0' }}>
                <span aria-hidden="true" style={{ width: 7, height: 7, borderRadius: '50%', background: headerMeta.accent }} />
                <span
                  className="text-slate-500 dark:text-slate-400"
                  style={{ fontSize: '0.625rem', fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase' }}
                >
                  {headerMeta.label}
                </span>
                <span
                  className="text-slate-500 dark:text-slate-300 bg-slate-100 dark:bg-slate-700"
                  style={{ fontSize: '0.625rem', fontWeight: 800, borderRadius: 9999, padding: '0 6px' }}
                >
                  {sectionCounts[section]}
                </span>
                <span className="bg-slate-200 dark:bg-slate-700" style={{ flex: 1, height: 1 }} />
              </div>
            )}
            <ProcedureChip
              sitting={sitting}
              viewer={viewer}
              primaryAction={primary}
              secondaryAction={secondary}
              noteAction={note}
              onAction={onAction}
              onDelete={handleDelete}
              canReassign={canReassign}
              canCancel={canCancel}
              compact={compact}
              lockReason={isLocked ? `Waiting for step ${lockInfo.blockedByStep}` : null}
              showPatient={showPatient}
            />
          </React.Fragment>
        );
      })}
    </div>
  );
}
